import {useFlags} from 'launchdarkly-react-client-sdk';
import React, {useState} from 'react';
import {Button, Headline, Input} from '@lightricks/react-design-system';
import raiseFlashMessage from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import {Brand} from '@/types/brand';
import Modal from '@/components/modal/Modal';
import {
  useUnlinkInstagram,
  useLinkShopify,
  useUnlinkShopify,
  useUnlinkTiktok,
  linkInstagram,
  linkTiktok,
} from '@/hooks/mutations/use-brand-integrations';
import useUpdateBrand from '@/hooks/mutations/use-update-brand';
import BrandAvatar from '../brand-avatar/BrandAvatar';
import styles from './ConfigureBrandModal.module.scss';
import ConnectedAccountRow from './ConnectedAccountRow';

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : undefined;
}

/**
 * Unwrap an Axios error to its response payload, falling back to the
 * original value for non-Axios errors.
 */
function unwrapAxiosError(error: unknown): unknown {
  const err = asRecord(error);
  const response = asRecord(err?.response);
  return response?.data ?? error;
}

function getErrorFullMessages(error: unknown): string[] | undefined {
  const data = unwrapAxiosError(error);
  const err = asRecord(data);
  const errors = asRecord(err?.errors);
  const fm = errors?.full_messages;
  if (Array.isArray(fm)) return fm as string[];
  return undefined;
}

function getErrorDetail(error: unknown): string | undefined {
  const data = unwrapAxiosError(error);
  const err = asRecord(data);
  const errors = err?.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    const first = asRecord(errors[0]);
    if (typeof first?.detail === 'string') return first.detail;
  }
  const errorsObj = asRecord(errors);
  if (typeof errorsObj?.detail === 'string') return errorsObj.detail;
  if (typeof err?.message === 'string') return err.message;
  return undefined;
}

interface ConfigureBrandModalProps {
  brand: Brand;
  agencyId: string;
  isOpen: boolean;
  onClose: () => void;
}

function ConfigureBrandModal({
  brand,
  agencyId,
  isOpen,
  onClose,
}: ConfigureBrandModalProps) {
  const flags = useFlags();
  const [brandName, setBrandName] = useState(brand.displayName);
  const [isTestBrand, setIsTestBrand] = useState(brand.isTestBrand);
  const [shopifyShopName, setShopifyShopName] = useState('');
  const [walmartAdvertiserId, setWalmartAdvertiserId] = useState('');

  const updateBrand = useUpdateBrand(agencyId);
  const unlinkInstagram = useUnlinkInstagram();
  const shopifyLink = useLinkShopify();
  const unlinkShopify = useUnlinkShopify();
  const unlinkTiktok = useUnlinkTiktok();

  const igAccount = brand.brandInstagramBusinessAccount;
  const instagramLinked = !!igAccount && igAccount.state === 'active';
  const shopifyLinked = brand.hasShopifyAccessToken;
  const tiktokAuth = brand.tiktokBusinessAuthorizations?.[0];
  const tiktokLinked =
    !!tiktokAuth &&
    (tiktokAuth.authorizationState === 'pending' ||
      tiktokAuth.authorizationState === 'success');
  const walmartLinked = !!brand.walmartConnectAdvertiserName;

  const handleSave = () => {
    updateBrand.mutate(
      {brandId: brand.id, data: {name: brandName, isTestBrand}},
      {
        onSuccess: () => {
          raiseFlashMessage({
            status: 'success',
            message: translate('Brand updated successfully.'),
          });
          onClose();
        },
        onError: () => {
          raiseFlashMessage({
            status: 'error',
            message: translate('There was an error updating the brand.'),
          });
          onClose();
        },
      }
    );
  };

  const handleUnlinkInstagram = () => {
    unlinkInstagram.mutate(
      {brandId: brand.id},
      {
        onSuccess: () => {
          raiseFlashMessage({
            status: 'success',
            message: translate('Account successfully unlinked.'),
          });
          onClose();
        },
        onError: (error: unknown) => {
          const fullMessages = getErrorFullMessages(error);
          if (fullMessages?.[0]?.includes('not allowed')) {
            raiseFlashMessage({
              status: 'error',
              message: translate(
                'You do not have permission to perform this action.'
              ),
            });
          } else {
            raiseFlashMessage({
              status: 'error',
              message:
                fullMessages?.[0] || translate('Error unlinking account'),
            });
          }
          onClose();
        },
      }
    );
  };

  const handleLinkShopify = () => {
    shopifyLink.mutate(
      {
        shopName: shopifyShopName,
        successRedirectUrl: window.location.href,
        merchantId: brand.id,
      },
      {
        onSuccess: (response) => {
          const data = asRecord(response);
          const nested = asRecord(data?.data);
          const redirectUrl =
            typeof nested?.redirect_url === 'string'
              ? nested.redirect_url
              : undefined;
          if (redirectUrl) {
            window.location.href = redirectUrl;
          }
        },
        onError: (error: unknown) => {
          const detail = getErrorDetail(error) || translate('Unknown error');
          raiseFlashMessage({status: 'error', message: detail});
        },
      }
    );
  };

  const handleUnlinkShopify = () => {
    unlinkShopify.mutate(
      {merchantId: brand.id},
      {
        onSuccess: () => {
          raiseFlashMessage({
            status: 'success',
            message: translate('Account successfully unlinked.'),
          });
          onClose();
        },
        onError: () => {
          raiseFlashMessage({
            status: 'error',
            message: translate('Error unlinking account'),
          });
        },
      }
    );
  };

  const handleUnlinkTiktok = () => {
    unlinkTiktok.mutate(
      {brandId: brand.id},
      {
        onSuccess: () => {
          raiseFlashMessage({
            status: 'success',
            message: translate('Account successfully unlinked.'),
          });
          onClose();
        },
        onError: (error: unknown) => {
          const fullMessages = getErrorFullMessages(error);
          if (fullMessages?.[0]?.includes('not allowed')) {
            raiseFlashMessage({
              status: 'error',
              message: translate(
                'You do not have permission to perform this action.'
              ),
            });
          } else {
            raiseFlashMessage({
              status: 'error',
              message:
                fullMessages?.[0] || translate('Error unlinking account'),
            });
          }
        },
      }
    );
  };

  const handleLinkWalmartConnect = () => {
    updateBrand.mutate(
      {
        brandId: brand.id,
        data: {
          walmartConnectAdvertiserAdvertiserId: walmartAdvertiserId,
        },
      },
      {
        onSuccess: () => {
          raiseFlashMessage({
            status: 'success',
            message: translate('Account successfully connected.'),
          });
          onClose();
        },
        onError: () => {
          raiseFlashMessage({
            status: 'error',
            message: translate(
              'There was an error connecting your Walmart Connect account.'
            ),
          });
        },
      }
    );
  };

  const handleUnlinkWalmartConnect = () => {
    updateBrand.mutate(
      {
        brandId: brand.id,
        data: {walmartConnectAdvertiserAdvertiserId: 'unlink'},
      },
      {
        onSuccess: () => {
          raiseFlashMessage({
            status: 'success',
            message: translate('Account successfully unlinked.'),
          });
          onClose();
        },
        onError: () => {
          raiseFlashMessage({
            status: 'error',
            message: translate(
              'There was an error unlinking your Walmart Connect account.'
            ),
          });
        },
      }
    );
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      showCloseButton
      size="small"
      contentClassName={styles.modal}
    >
      <div className={styles.container}>
        <Headline size="lg" data-test-configure-brand-modal-header>
          {translate('Configure Brand')}
        </Headline>

        <div className={styles.brandIdentity}>
          <div className={styles.avatarRow}>
            <BrandAvatar brand={brand} agencyId={agencyId} size="xl" />
            <div className={styles.nameSection}>
              <label className={styles.label}>{translate('Brand Name')}</label>
              <Input
                value={brandName}
                onInputChange={(val: string) => setBrandName(val)}
                placeholder={brand.displayName}
                testID="configure-brand-modal-brand-name"
                data-test-configure-brand-modal-brand-name
              />
            </div>
          </div>

          {flags.internalOnlyFeatures && (
            <label className={styles.testBrandCheckbox}>
              <input
                type="checkbox"
                checked={isTestBrand}
                onChange={() => setIsTestBrand(!isTestBrand)}
              />
              {translate('Test Brand')}
            </label>
          )}

          <Button
            appearance="brand"
            mode="filled"
            size="medium"
            onClick={handleSave}
            data-test-configure-brand-modal-save-button
          >
            {translate('Save Changes')}
          </Button>
        </div>

        <hr className={styles.divider} />

        <div className={styles.connectedAccounts}>
          <Headline size="sm">{translate('Connected Accounts')}</Headline>

          <ConnectedAccountRow
            name="Instagram"
            description={translate(
              'Link your Instagram Business Account for Partnership Ads'
            )}
            isConnected={instagramLinked}
            onLink={() =>
              linkInstagram(brand.id, !!flags.partnershipAdsEnabled)
            }
            onUnlink={handleUnlinkInstagram}
            linkLabel={translate('Link Instagram')}
            linkTestId="configure-brand-modal-link-instagram"
            unlinkTestId="configure-brand-modal-unlink-instagram"
            confirmationTestId="configure-brand-modal-instagram-confirmation"
          />

          <ConnectedAccountRow
            name="Shopify"
            description={translate(
              'Connect your Shopify store to manage products and orders'
            )}
            isConnected={shopifyLinked}
            connectedLabel={brand.shopifyShopName}
            onLink={handleLinkShopify}
            onUnlink={handleUnlinkShopify}
            linkLabel={translate('Link Shopify')}
            linkDisabled={!shopifyShopName}
            linkTestId="configure-brand-modal-link-shopify"
            unlinkTestId="configure-brand-modal-unlink-shopify"
            confirmationTestId="configure-brand-modal-shopify-confirmation"
            inputField={
              <input
                value={shopifyShopName}
                onChange={(e) =>
                  setShopifyShopName(
                    e.target.value.replace(/[^a-zA-Z0-9-]/g, '')
                  )
                }
                placeholder={translate('your-shop-name')}
                data-test-configure-brand-modal-shopify-shop-name-input
              />
            }
          />

          <ConnectedAccountRow
            name="TikTok"
            description={translate('Connect your TikTok Business Account')}
            isConnected={tiktokLinked}
            onLink={() => linkTiktok(brand.id)}
            onUnlink={handleUnlinkTiktok}
            linkLabel={translate('Link TikTok')}
            linkTestId="configure-brand-modal-link-tiktok"
            unlinkTestId="configure-brand-modal-unlink-tiktok"
            confirmationTestId="configure-brand-modal-tiktok-confirmation"
          />

          <ConnectedAccountRow
            name="Walmart Connect"
            description={translate(
              'Connect your Walmart Connect advertiser account'
            )}
            isConnected={walmartLinked}
            connectedLabel={brand.walmartConnectAdvertiserName}
            onLink={handleLinkWalmartConnect}
            onUnlink={handleUnlinkWalmartConnect}
            linkLabel={translate('Link Walmart Connect')}
            linkDisabled={!walmartAdvertiserId}
            linkTestId="configure-brand-modal-link-walmart-connect"
            unlinkTestId="configure-brand-modal-unlink-walmart-connect"
            confirmationTestId="configure-brand-modal-walmart-connect-confirmation"
            inputField={
              <input
                value={walmartAdvertiserId}
                onChange={(e) => setWalmartAdvertiserId(e.target.value)}
                placeholder={translate('Advertiser ID')}
              />
            }
          />
        </div>
      </div>
    </Modal>
  );
}

export default ConfigureBrandModal;
